'use client'

import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import { TranslationFunction } from '@/src/types/translations'
import { Box, Button, Divider, IconButton, Typography } from '@mui/material'
import React from 'react'

import type { HotelRoomConfig } from '../searchForm/hooks/useHotelSearchManager'

interface HotelGuestsSelectorProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    roomConfigs: HotelRoomConfig[]
    minRooms: number
    maxRooms: number
    minAdultsPerRoom: number
    maxAdultsPerRoom: number
    minChildrenPerRoom: number
    maxChildrenPerRoom: number
    onRoomCountChange: (count: number) => void
    onRoomAdultsChange: (roomIndex: number, adults: number) => void
    onRoomChildrenChange: (roomIndex: number, children: number) => void
    onApply: () => void
    t: TranslationFunction<'common'>
}

interface MobileCounterRowProps {
    label: string
    hint?: string
    value: number
    canDecrease: boolean
    canIncrease: boolean
    onDecrease: () => void
    onIncrease: () => void
}

const MobileCounterRow: React.FC<MobileCounterRowProps> = ({
    label,
    hint,
    value,
    canDecrease,
    canIncrease,
    onDecrease,
    onIncrease
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
        <Box>
            <Typography variant="body1" fontWeight={500}>
                {label}
            </Typography>
            {hint && (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
                onClick={onDecrease}
                disabled={!canDecrease}
                sx={{
                    border: '1px solid',
                    borderColor: !canDecrease ? 'grey.300' : 'grey.400',
                    borderRadius: '50%',
                    width: 36,
                    height: 36
                }}
            >
                <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center' }}>
                {value}
            </Typography>
            <IconButton
                onClick={onIncrease}
                disabled={!canIncrease}
                sx={{
                    border: '1px solid',
                    borderColor: !canIncrease ? 'grey.300' : 'primary.main',
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    color: !canIncrease ? 'grey.400' : 'primary.main'
                }}
            >
                <AddIcon fontSize="small" />
            </IconButton>
        </Box>
    </Box>
)

const HotelGuestsSelector: React.FC<HotelGuestsSelectorProps> = ({
    open,
    onOpen,
    onClose,
    roomConfigs,
    minRooms,
    maxRooms,
    minAdultsPerRoom,
    maxAdultsPerRoom,
    minChildrenPerRoom,
    maxChildrenPerRoom,
    onRoomCountChange,
    onRoomAdultsChange,
    onRoomChildrenChange,
    onApply,
    t
}) => {
    const roomCount = roomConfigs.length

    return (
        <SwipeableDrawerWithBackNavigation
            anchor="bottom"
            open={open}
            onClose={onClose}
            onOpen={onOpen}
            sx={{ height: 'fit-content', maxHeight: '85dvh' }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85dvh' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, px: 3, pt: 2, pb: 1 }}>
                    {t('rooms')} & {t('guest', { s: 's' })}
                </Typography>

                <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.100', mx: 2, borderRadius: 1 }}>
                    <MobileCounterRow
                        label={t('howManyRooms')}
                        value={roomCount}
                        canDecrease={roomCount > minRooms}
                        canIncrease={roomCount < maxRooms}
                        onDecrease={() => onRoomCountChange(roomCount - 1)}
                        onIncrease={() => onRoomCountChange(roomCount + 1)}
                    />
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 1 }}>
                    {roomConfigs.map((room, index) => (
                        <Box key={index}>
                            {index > 0 && <Divider sx={{ my: 1 }} />}
                            <Typography variant="body2" fontWeight={600} sx={{ pt: index > 0 ? 1 : 0, pb: 0.5 }}>
                                {t('roomLabel', { number: index + 1 })}
                            </Typography>
                            <MobileCounterRow
                                label={t('adult')}
                                hint={t('adultsAgeHint')}
                                value={room.adults}
                                canDecrease={room.adults > minAdultsPerRoom}
                                canIncrease={room.adults < maxAdultsPerRoom}
                                onDecrease={() => onRoomAdultsChange(index, room.adults - 1)}
                                onIncrease={() => onRoomAdultsChange(index, room.adults + 1)}
                            />
                            <MobileCounterRow
                                label={t('child')}
                                hint={t('childrenAgeHint')}
                                value={room.children}
                                canDecrease={room.children > minChildrenPerRoom}
                                canIncrease={room.children < maxChildrenPerRoom}
                                onDecrease={() => onRoomChildrenChange(index, room.children - 1)}
                                onIncrease={() => onRoomChildrenChange(index, room.children + 1)}
                            />
                        </Box>
                    ))}
                </Box>

                <Box sx={{ p: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                        fullWidth
                        variant="customVariantPrimary"
                        size="large"
                        onClick={onApply}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        {t('apply')}
                    </Button>
                </Box>
            </Box>
        </SwipeableDrawerWithBackNavigation>
    )
}

export { HotelGuestsSelector }
export type { HotelGuestsSelectorProps }
